import React, { useRef, useEffect, useState } from "react";
import api from "../../services/api";

import errors, { IAliancaErrors } from "../../Utils/ExtractErrors";

import { Form, Modal, Button, Grid, Image, Loader, Dimmer, Label, Input, DropdownProps, TextArea, TextAreaProps, Segment, Divider, InputProps, Placeholder, PlaceholderLine } from "semantic-ui-react";
import { toast } from "react-toastify";

import ModalApp from "../../components/ModalApp";
import ImageZoom from "../../components/ImageZoom";
import ModalYesNo from "../../components/ModalYesNo";
import ModeloKit from "../../assets/modeloKit.png";
import ModeloPromissoria from "../../assets/modeloPromissoria.png";

import ResellersRepository, { IResellerListModel } from "../../Repositories/ResellersRepository";
import KitRepository, { IKitListModel } from "../../Repositories/KitRepository";
import ImagemRepository from "../../Repositories/ImagesRepository";

import { IKit, IImagem, ISearchResponse, eStatusKit, eStatusLiberacao } from "../../interfaces";
import { GetUser } from "../../services/auth";
import EditarPessoa from "../../components/EditarPessoa";
import CustomLoader from "../../components/CustomLoader";


interface State extends IKit {
    images: IImagem[];
    kitsWithReseller: ISearchResponse<IKitListModel> | undefined;
    revendedorDropdown: string | undefined;
    cidade: string;
    uf: string;
    apiErrors: IAliancaErrors;
}

interface Props {
    kitId: string;
    onSuccess: () => void;
}

const initialState: State = {
    id: "",
    data: "",
    numero: 0,
    valor: 0,
    statusKit: 0,
    pedidoId: "",
    revendedorId: "",
    revendedorDropdown: "",
    images: [],
    cidade: "",
    uf: "",
    prorrogado: 0,
    observacao: "",
    nomeOperador: "",
    ficha: false,
    liberacao: 0,
    kitsWithReseller: undefined,
    apiErrors: { errors: [] }
}

const EditarKit: React.FC<Props> = ({ kitId, onSuccess }) => {

    const [open, setOpen] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [state, setState] = useState<State>(initialState);
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
    const [preview, setPreview] = useState<string[]>([]);
    const [revendedores, setRevendedores] = useState<IResellerListModel[]>([]);

    const fileInputRef = useRef<HTMLInputElement>(null);

    const { role } = GetUser();

    const load = async () => {
        setLoading(true);

        try {

            const [kit, kitsWithReseller, revendedoresAtivos, images] = await Promise.all([
                KitRepository.GetById(kitId),
                KitRepository.List({
                    filter: `StatusKit == 3`
                }),
                ResellersRepository.List({
                    filter: ""
                }),
                ImagemRepository.GetByReferenceId(kitId)
            ]);

            setRevendedores(revendedoresAtivos.list);

            if (kit.revendedorId !== undefined) {
                const findRevendedor = revendedoresAtivos.list.find(revendedor => revendedor.id === kit.revendedorId);

                setState({
                    ...state,
                    ...kit,
                    images,
                    revendedorDropdown: kit.revendedorId,
                    cidade: findRevendedor?.cidade || "",
                    uf: findRevendedor?.uf || "",
                    kitsWithReseller
                });
            }


        } catch (err) {
            if (err.response.status === 401) {
                window.location.href = "/";
            }

            const error = errors.getAllErrors(err.response.data);

            if (error !== undefined)
                toast(error, { position: "top-center", type: "error" });
        }
        setLoading(false);
    }

    const onButtonClick = () => {
        if (fileInputRef && fileInputRef.current) {
            fileInputRef.current.click();
        }
    }

    useEffect(() => {
        if (selectedFiles.length > 0) {
            let images: string[] = [];

            for (const file of selectedFiles) {
                images.push(URL.createObjectURL(file));
            }

            setPreview(preview => preview.concat(images));

            return () => {
                for (const image of images) { URL.revokeObjectURL(image) }
                setPreview([]);
            }
        }
    }, [selectedFiles])

    const onSelectFile = (e: any) => {
        if (!e.target.files || e.target.files.length === 0) {
            setSelectedFiles([])
            return

        }

        let files: File[] = [];
        files = files.concat(selectedFiles);

        for (const file of e.target.files) {
            files.push(file);
        }

        setSelectedFiles(files);
    }

    const onRemovePreview = (imageUrl: string, index: number) => {
        const images = preview.filter(x => x !== imageUrl);

        selectedFiles.splice(index, 1);

        URL.revokeObjectURL(imageUrl);
        setPreview(images);
    }

    const onRemove = async (id: string) => {
        await ImagemRepository.DeleteById(id);
        load();
    }

    const handleSubmitObs = async () => {
        try {
            const response = await KitRepository.UpdateObs({
                id: kitId,
                kitPutModelObs: { observacao: state.observacao }
            });
            toast(`💾 ${response.data.message}`, { position: "top-center", type: "success" });
        } catch (error) {
            return toast("❌ Erro ao atualizar a observação.", { position: "top-center", type: "error" });
        }
    }

    const handleSubmitFicha = async () => {
        try {
            const response = await KitRepository.UpdateFicha({
                id: kitId,
                kitPutModelFicha: { ficha: state.ficha }
            });
            toast(`💾 ${response.data.message}`, { position: "top-center", type: "success" });
            setOpen(false);
            onSuccess();
        } catch (error) {
            return toast("❌ Erro ao atualizar o status da ficha.", { position: "top-center", type: "error" });
        }
    }

    const handleSubmitForm = async () => {
        const hasRevendedor = state.revendedorId !== null || state.revendedorDropdown?.length;
        const hasTwoFiles = selectedFiles.length >= 2 || state.images.length >= 2;
        if (!hasRevendedor) return toast("❌ É necessário selecionar um revendedor.", { position: "top-center", type: "error" });
        if (!hasTwoFiles) return toast("❌ É necessário inserir no mínimo 2 fotos.", { position: "top-center", type: "error" });
        try {
            setLoading(true);
            if (role === "Representante") {
                state.liberacao = eStatusLiberacao.Pendente;
            }
            if (role === "Admin" && state.statusKit === eStatusKit.Representante && state.liberacao === 2) {
                state.statusKit = eStatusKit.Revendedora;
            }
            const response = await KitRepository.Update({
                id: kitId,
                kitPutModel: { revendedorId: state.revendedorDropdown, statusKit: state.statusKit, liberacao: state.liberacao }
            });

            if (response.status === 200) {
                const data = new FormData();

                for (const file of selectedFiles) {
                    data.append("files", file);
                }

                setSelectedFiles([]);
                setPreview([]);

                await api.post(`imagem/${kitId}`, data);

                toast(`🎉 ${response.data.message}`, { position: "top-center", type: "success" });
                setOpen(false);
                onSuccess();
            }
        } catch (err) {
            if (err.response.status === 401) {
                window.location.href = "/";
            }

            setState({ ...state, apiErrors: err.response.data });

            const error = errors.getAllErrors(err.response.data);

            if (error !== undefined)
                toast(error, { position: "top-center", type: "error" });
        }
        setLoading(false);
    }

    const handleOpen = () => {
        load();

        setOpen(true);
    }

    const handleClose = () => {
        setState({ ...state, apiErrors: { errors: [] } });
        setOpen(false);
        setSelectedFiles([]);
        setPreview([]);
    }

    const hasError = (key: string) => {
        return errors.hasError(state.apiErrors, key);
    }

    const getMessage = (key: string) => {
        return errors.getMessage(state.apiErrors, key);
    }

    const onChange = (event: React.SyntheticEvent<HTMLElement, Event>, data: InputProps) => {
        const { name, value } = data;
        setState(state => ({ ...state, [name]: value }));
    }

    const onChangeFicha = (value: boolean) => {
        setState({ ...state, ficha: value });
    }

    const onChangeTextArea = (event: React.SyntheticEvent<HTMLElement, Event>, data: TextAreaProps) => {
        const { name, value } = data;
        setState(state => ({ ...state, [name]: value }));
    }

    const onRevendedorChange = (event: React.SyntheticEvent<HTMLElement, Event>, data: DropdownProps) => {
        const { name, value } = data;

        const findRevendedor = revendedores.find(revendedor => revendedor.id === value);

        setState({
            ...state,
            [name]: value,
            cidade: findRevendedor?.cidade || "",
            uf: findRevendedor?.uf || "",
            apiErrors: { errors: [] }
        });
    }

    const editarContent = (
        <Modal.Content>
            {loading &&
                <CustomLoader />}
            {!loading &&
                <>
                    <Form>
                        {
                            role !== "GerenteS" &&
                            <Grid.Row>
                                <Form.Group widths="16">
                                    <Form.Field width={9} error={hasError("RevendedorId")} required>
                                        {state.revendedorId === null
                                            ? <Form.Dropdown
                                                fluid
                                                selection
                                                search
                                                scrolling
                                                name="revendedorDropdown"
                                                noResultsMessage="Nenhum registro encontrado."
                                                placeholder="Selecione"
                                                label="Revendedor"
                                                value={state.revendedorDropdown}
                                                onChange={onRevendedorChange}
                                                options={revendedores.filter(revendedor => revendedor.status === 1 && revendedor.devedor === 0).map(revendedor => { return { key: revendedor.id, value: revendedor.id, text: revendedor.nome } })}
                                            />
                                            : <>
                                                <label>Revendedor</label>
                                                <Input
                                                    fluid
                                                    readOnly
                                                    value={revendedores.find(revendedor => revendedor.id === state.revendedorId)?.nome}
                                                />
                                            </>
                                        }
                                        {hasError("RevendedorId") && <Label pointing prompt>{getMessage("RevendedorId")}</Label>}
                                    </Form.Field>
                                    <Form.Field width={4}>
                                        <label>Cidade</label>
                                        <Input
                                            readOnly
                                            placeholder="Cidade"
                                            value={state.cidade}
                                        />
                                    </Form.Field>
                                    <Form.Field width={2}>
                                        <label>Estado</label>
                                        <Input
                                            readOnly
                                            placeholder="Estado"
                                            value={state.uf}
                                        />
                                    </Form.Field>
                                    {
                                        ((role === "Admin" || role === "Operador") && state.revendedorDropdown?.length !== undefined) &&
                                        <Form.Field width={1}>
                                            <label>Cad.</label>
                                            <EditarPessoa
                                                id={state.revendedorId || ""}
                                                url={`/revendedor/${state.revendedorId}`}
                                                onSuccess={load} />
                                        </Form.Field>
                                    }
                                </Form.Group>
                            </Grid.Row>
                        }
                        <Grid.Row>
                            <Form.Group>
                                <Form.Field width="16">
                                    <label>Observação</label>
                                    {role === "Admin"
                                        ? <Input
                                            fluid
                                            name="observacao"
                                            placeholder="Adicione a observação ..."
                                            onChange={onChange}
                                            value={state.observacao}
                                            action={
                                                <Button
                                                    icon="save"
                                                    color="green"
                                                    onClick={handleSubmitObs}
                                                />
                                            }
                                        />
                                        :
                                        <TextArea
                                            fluid
                                            readOnly
                                            name="observacao"
                                            placeholder="Adicione a observação ..."
                                            onChange={onChangeTextArea}
                                            value={state.observacao}
                                        />}
                                </Form.Field>
                            </Form.Group>
                            {
                                role === "Admin" &&
                                <Form.Group>
                                    <Form.Field width="16">
                                        <label>Ficha retornou?</label>
                                        <Button.Group fluid widths="13">
                                            <Button
                                                color={state.ficha === false ? "black" : "grey"}
                                                icon="thumbs down"
                                                content="NÃO"
                                                onClick={() => onChangeFicha(false)}
                                            />
                                            <Button
                                                color={state.ficha === true ? "green" : "grey"}
                                                icon="thumbs up"
                                                content="SIM"
                                                onClick={() => onChangeFicha(true)}
                                            />
                                            <Button
                                                content="Salvar ficha"
                                                icon="check"
                                                color="pink"
                                                onClick={handleSubmitFicha} />
                                        </Button.Group>
                                    </Form.Field>
                                </Form.Group>
                            }
                            <Divider />
                            <Form.Group widths="equal">
                                {
                                    role === "Admin" &&
                                    <>
                                        <Form.Field>
                                            <Button
                                                fluid
                                                content="Selecionar fotos"
                                                icon="images"
                                                onClick={onButtonClick}
                                                color="brown"
                                            />
                                            <input
                                                ref={fileInputRef}
                                                type="file"
                                                hidden
                                                multiple
                                                accept="image/*"
                                                onChange={onSelectFile}
                                            />
                                        </Form.Field>
                                        <Form.Field>
                                            <Button.Group fluid>
                                                <Button
                                                    disabled={state.statusKit !== 2}
                                                    color={state.liberacao === 2 ? "green" : "grey"}
                                                    icon="thumbs up"
                                                    content="Liberado"
                                                    onClick={() => { setState({ ...state, liberacao: 2 }) }}
                                                />
                                                <Button.Or text="?" />
                                                <Button
                                                    disabled={state.statusKit !== 2}
                                                    color={state.liberacao === 3 ? "black" : "grey"}
                                                    icon="thumbs down"
                                                    content="Não Liberado"
                                                    onClick={() => { setState({ ...state, liberacao: 3 }) }}
                                                />
                                            </Button.Group>
                                        </Form.Field>
                                    </>
                                }
                                {
                                    role !== "Admin" &&
                                    <Form.Field width="16">
                                        <Button
                                            fluid
                                            content="Selecionar fotos"
                                            icon="images"
                                            onClick={onButtonClick}
                                            color="black"
                                        />
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            hidden
                                            multiple
                                            accept="image/*"
                                            onChange={onSelectFile}
                                        />
                                    </Form.Field>
                                }
                            </Form.Group>
                        </Grid.Row>

                        <Segment placeholder>
                            <Grid columns={2} stackable>
                                <Divider vertical></Divider>
                                <Grid.Row>
                                    <Grid.Column>
                                        <Grid.Row>
                                            <p>
                                                <strong>
                                                    FOTOS NECESSÁRIAS:<br />
                                                    - FOTO DE ROSTO SEGURANDO O KIT<br />
                                                    - FOTO DA PROMISSÓRIA ASSINADA.
                                                </strong>
                                            </p>
                                            <Grid columns="2" doubling>
                                                <Grid.Column>
                                                    <Image centered src={ModeloKit} size="medium" />
                                                </Grid.Column>
                                                <Grid.Column>
                                                    <Image centered src={ModeloPromissoria} size="medium" />
                                                </Grid.Column>
                                            </Grid>
                                        </Grid.Row>
                                    </Grid.Column>
                                    <Grid.Column>
                                        <p>
                                            <strong>
                                                FOTOS:<br />
                                            </strong>
                                        </p>
                                        <Grid columns="2" doubling>
                                            {state.images.length > 0 &&
                                                state.images.map((x, index) =>
                                                    <Grid.Column key={`image-${index}`}>
                                                        <Image
                                                            fluid
                                                            label={<ImageZoom imagePath={`${api.defaults.baseURL}/images/${x.arquivo}`} />}
                                                            src={`${api.defaults.baseURL}/images/${x.arquivo}`}
                                                        />
                                                        <ModalYesNo
                                                            trigger={<Button icon="trash" color="red" attached="bottom" disabled={(role !== "Admin" && role !== "Operador")} />}
                                                            header="Apagar"
                                                            content="Deseja realmente apagar esta foto?"
                                                            onSuccess={() => onRemove(x.id || "")}
                                                        />
                                                    </Grid.Column>
                                                )}
                                            {preview.length > 0 &&
                                                preview.map((x, index) =>
                                                    <Grid.Column key={`image-${index}`}>
                                                        <Image
                                                            fluid
                                                            label={<ImageZoom imagePath={x} />}
                                                            src={x}
                                                        />
                                                        <ModalYesNo
                                                            trigger={<Button icon="trash" color="red" attached="bottom" />}
                                                            header="Apagar"
                                                            content="Deseja realmente apagar esta foto?"
                                                            onSuccess={() => onRemovePreview(x, index)}
                                                        />
                                                    </Grid.Column>
                                                )}
                                        </Grid>
                                    </Grid.Column>
                                </Grid.Row>
                            </Grid>
                        </Segment>
                    </Form>
                </>}
        </Modal.Content>
    );

    return (
        <ModalApp
            open={open}
            handleOpen={handleOpen}
            handleClose={handleClose}
            title={loading ? "" : `Editar kit: ${state.numero}`}
            content={editarContent}
            trigger={{
                icon: "pencil",
                color: "pink"
            }}
            onSuccess={{ content: "Salvar", disabled: role !== "GerenteS" ? false : true, func: handleSubmitForm }}
        />
    );
};

export default EditarKit;
